import { BehaviorSubject, Observable, throwError } from 'rxjs';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import {
  catchError,
  filter,
  finalize,
  map,
  switchMap,
  take,
} from 'rxjs/operators';

import { AppState } from '../reducers';
import { AppVersion } from './constants';
import { AuthService } from './services/auth.service';
import { BannerService } from '@services/banner.service';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { StoreService } from '@services/store.service';
import { environment } from '../../environments/environment';
import { logout } from './actions/auth.actions';
import { TabSessionService } from './services/tab-session.service';

/** Result of a refresh cycle, broadcast to requests queued behind it */
type RefreshOutcome = { token: string } | { error: unknown };

@Injectable()
export class SensikaHttpInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  // null = refresh in progress (acts as the queue "lock"); queued requests wait
  // for an outcome — success retries them, failure propagates the error so they
  // never hang (previously a failed refresh left them pending forever).
  private refreshOutcome$: BehaviorSubject<RefreshOutcome | null> =
    new BehaviorSubject<RefreshOutcome | null>(null);

  constructor(
    private authService: AuthService,
    private storeService: StoreService,
    private banners: BannerService,
    private store: Store<AppState>,
    private tabSessionService: TabSessionService,
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    // 1. Prepare initial request headers
    let mutated = this.addBasicHeaders(req);

    // 2. Add Base URL
    if (this.isRelativeUrl(mutated.url) && !mutated.url.includes('assets/')) {
      mutated = mutated.clone({ url: environment.apiBaseUrl + mutated.url });
    }

    // 3. Handle Loading Spinner
    const showLoading = mutated.headers.get('X-Skip-Loading') !== 'true';
    if (showLoading) this.storeService.requestStarted();

    return next.handle(mutated).pipe(
      // Unwrap backend envelope: { body: { ...data } } -> { ...data }
      map((event) => this.unwrapEnvelope(event)),

      catchError((error: unknown) => {
        if (!(error instanceof HttpErrorResponse)) {
          return throwError(() => error);
        }

        // Trigger refresh only on 401, excluding auth endpoints where a 401
        // means "credentials wrong / session dead", not "token stale"
        if (error.status === 401 && !this.isAuthEndpointUrl(mutated.url)) {
          return this.handle401Error(mutated, next);
        }

        return this.handleError(mutated, error);
      }),

      finalize(() => {
        if (showLoading) this.storeService.requestEnded();
      }),
    );
  }

  /**
   * Orchestrates the token refresh or waits if one is already in progress.
   */
  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshOutcome$.next(null);

      const staleToken = this.extractBearerToken(request);

      return this.authService.refreshSession(staleToken).pipe(
        switchMap((token: string) => {
          this.isRefreshing = false;
          this.refreshOutcome$.next({ token });
          return this.retryWithFreshHeaders(request, next);
        }),
        catchError((err) => {
          this.isRefreshing = false;
          // Unblock queued requests with the failure instead of leaving them
          // (and the loading spinner) hanging forever
          this.refreshOutcome$.next({ error: err });
          this.store.dispatch(logout());
          return throwError(() => this.normalizeError(err));
        }),
      );
    } else {
      return this.refreshOutcome$.pipe(
        filter((outcome): outcome is RefreshOutcome => outcome !== null),
        take(1),
        switchMap((outcome) =>
          'error' in outcome
            ? throwError(() => this.normalizeError(outcome.error as any))
            : this.retryWithFreshHeaders(request, next),
        ),
      );
    }
  }

  /** Retry with full headers (fresh token, X-Company-Id, ...) */
  private retryWithFreshHeaders(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return next.handle(this.addBasicHeaders(request)).pipe(
      map((event) => this.unwrapEnvelope(event)),
      catchError((err) => this.handleError(request, err)),
    );
  }

  private extractBearerToken(request: HttpRequest<any>): string | null {
    const header = request.headers.get('Authorization');
    return header?.replace(/^Bearer\s+/i, '') ?? null;
  }

  /**
   * Helper to attach token and standard headers
   */
  private addBasicHeaders(req: HttpRequest<any>): HttpRequest<any> {
    const credentials = this.authService.getCredentials();
    const language = this.authService.getLanguage();
    const token = credentials?.token ?? '';

    let headers = req.headers
      .set('X-App-Version', AppVersion.V3.toString())
      .set('Accept', 'application/json');

    if (token && !this.isPublicEndpointUrl(req)) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    if (language) {
      headers = headers.set('X-Language-ID', language.id.toString());
    }

    const companyId = this.tabSessionService.getActiveCompanyId();
    if (companyId) {
      headers = headers.set('X-Company-Id', companyId);
    }

    return req.clone({ headers, withCredentials: true });
  }

  /**
   * Strips the API's `{body: …}` envelope.
   *
   * Tests for the key, not its truthiness: payloads that are legitimately `0`, `''`,
   * `false` or `null` must still be unwrapped. Checking `event.body?.body` left those
   * responses wrapped, so consumers received the envelope object instead of the value.
   */
  private unwrapEnvelope(event: HttpEvent<any>): HttpEvent<any> {
    const isEnvelope =
      event instanceof HttpResponse &&
      event.body !== null &&
      typeof event.body === 'object' &&
      'body' in event.body;

    return isEnvelope
      ? (event as HttpResponse<any>).clone({ body: event.body.body })
      : event;
  }

  // --- Utility & Error Normalization ---

  private isRelativeUrl(url: string): boolean {
    return !/^(?:[a-z][a-z0-9+.-]*:)?\/\//i.test(url);
  }

  /**
   * Auth endpoints whose 401s must never trigger the token-refresh flow.
   * NOTE: the login matcher previously checked the stale '/user/auth' URL, so a
   * wrong-password 401 kicked off a doomed refresh + logout on the login page.
   */
  /**
   * Public endpoints that must never carry the bearer token: a stale token
   * would turn them into 401s (e.g. the login screen's i18n load right after
   * session expiry) even though they need no auth at all.
   */
  private isPublicEndpointUrl(req: HttpRequest<any>): boolean {
    return req.method === 'GET' && /\/utils\/i18n(\?|$)/.test(req.url);
  }

  private isAuthEndpointUrl(url: string): boolean {
    return (
      url.includes('/auth/refresh') ||
      url.includes('/auth/login') ||
      url.includes('/auth/auth_token') ||
      url.includes('/auth/logout')
    );
  }

  private handleError(
    req: HttpRequest<any>,
    error: HttpErrorResponse,
  ): Observable<never> {
    const normalized = this.normalizeError(error);
    if (error.status >= 500) {
      this.banners.showBanner?.({
        level: 'error',
        message: normalized.message,
        autoCloseMs: 5000,
        details: normalized.details,
        cta: { type: 'secondary', i18nKey: 'actions.close', action: 'close' },
      });
    }
    return throwError(() => normalized);
  }

  private normalizeError(err: HttpErrorResponse): any {
    if (err.status === 0 && !err.error) {
      return {
        code: 'network.unreachable',
        httpCode: 0,
        message: 'Network error. Please check your connection.',
        details: { originalMessage: err.message },
      };
    }

    const body = (err.error?.body ?? err.error ?? {}) as any;
    const httpCode = err.status || 500;
    const message = body?.message || this.defaultMessageForStatus(httpCode);

    return {
      code: body.code || (httpCode >= 500 ? 'server.fault' : undefined),
      httpCode,
      message,
      details: body.details,
      requestId: err.headers?.get('X-Request-Id') || body.requestId,
      module: body.module,
      action: body.action,
      level: body.level,
      response: body,
    };
  }

  private defaultMessageForStatus(status: number): string {
    const messages: Record<number, string> = {
      400: 'Invalid request.',
      401: 'Authentication required.',
      403: 'You do not have permission for this action.',
      409: 'Conflict detected.',
      422: 'Please correct the highlighted fields.',
      429: 'Too many requests. Try again shortly.',
      502: 'Network connection problem or server request is blocked.',
    };
    return messages[status] || 'Unexpected server error.';
  }
}
